import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef
} from 'react';
import Fuse, { IFuseOptions } from 'fuse.js';
import styled, { css } from 'styled-components';
import { debounce } from 'lodash';
import { useFormik } from 'formik';
import { Grid, Paragraph } from '../UIKit/system';
import { useSearchSetState, useSearchState } from '../../context/search';
import { ResultsSection } from './result-section';
import Container from '../container';
import { getThemeColor, inputReset } from '../../utils/styles';
import { ResultRail } from './result-rail';
import IconButton from '../icon-button';
import mq from '../../utils/mq';
import Icon from '../icon';

interface SearchSectionProps {
  searchData: Record<string, any[]>;
}

export const SearchSection: FC<PropsWithChildren<SearchSectionProps>> = (
  props
) => {
  const { searchData, children } = props;
  const searchState = useSearchState();
  const setSearchState = useSearchSetState();
  const inputWrapRef = useRef<HTMLDivElement>(null);

  /**
   * Append search term in URL for
   * Google Analytics to track the search event.
   */
  const updateUrl = useRef(
    debounce((query: string) => {
      const searchUrl = new URL(window.location.href);

      if (query) {
        searchUrl.searchParams.set('search', query);
        window.history.replaceState({ query }, '', searchUrl.toString());
      } else if (!query && searchUrl.searchParams.get('search')) {
        searchUrl.searchParams.delete('search');
        window.history.replaceState(null, '', searchUrl.toString());
      }
    }, 800)
  ).current;

  useEffect(() => {
    if (searchState && inputWrapRef.current) {
      inputWrapRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [searchState]);

  const form = useFormik({
    initialValues: { query: '' },
    onSubmit: () => void 0
  });

  useEffect(() => {
    updateUrl.cancel();
    updateUrl(form.values.query);
  }, [form.values.query]);

  const fuse = useMemo(() => {
    const options = {
      keys: ['title', 'description', 'metadata']
    } satisfies IFuseOptions<any>;
    const productsIndex = Fuse.createIndex(options.keys, searchData.products);
    const downloadsIndex = Fuse.createIndex(options.keys, searchData.downloads);
    const articlesIndex = Fuse.createIndex(options.keys, searchData.articles);

    return {
      products: new Fuse(searchData.products, options, productsIndex),
      downloads: new Fuse(searchData.downloads, options, downloadsIndex),
      articles: new Fuse(searchData.articles, options, articlesIndex)
    };
  }, [searchData]);

  const result = useMemo(() => {
    if (!form.values.query) return null;

    const products = performSearch(fuse.products, form.values.query, 8);
    const downloads = performSearch(fuse.downloads, form.values.query, 4);
    const articles = performSearch(fuse.articles, form.values.query, 4);

    if (
      products.length === 0 &&
      downloads.length === 0 &&
      articles.length === 0
    )
      return null;

    return {
      products,
      downloads,
      articles
    };
  }, [form.values]);

  const onExitSearch = () => {
    if (result) return;

    setSearchState(false);
  };

  return (
    <>
      <Section open={searchState} onMouseLeave={onExitSearch}>
        <Container large>
          <Header ref={inputWrapRef}>
            <Heading>Search</Heading>

            <HeaderContent>
              <form onSubmit={form.handleSubmit}>
                <InputWrap>
                  <Icon
                    name="magnify"
                    fillColor="black"
                    mr={14}
                    alignSelf="center"
                  />
                  <Input
                    {...form.getFieldProps('query')}
                    placeholder="Search for products, insights, and supporting documents"
                  />
                  <ClearButton
                    onClick={() => form.setFieldValue('query', '')}
                    hidden={!form.values.query}
                  />
                </InputWrap>
              </form>

              <IconButton
                icon={{ name: 'cross', fillColor: 'silver' }}
                size={14}
                onClick={() => setSearchState(false)}
                alignSelf="center"
                justifySelf="end"
                aria-label="Close search"
                title="Close search"
                display={['none', 'none', 'inherit']}
              />
            </HeaderContent>
          </Header>
        </Container>

        {result && (
          <ResultsWrap>
            <ResultRail heading="Products" results={result.products} />
            <ResultsSection heading="Downloads" results={result.downloads} />
            <ResultsSection heading="Articles" results={result.articles} />
          </ResultsWrap>
        )}
      </Section>
      <div hidden={!!result && searchState}>{children}</div>
    </>
  );
};

const performSearch = <TypeFuse extends Fuse<any>>(
  fuse: TypeFuse,
  term: string,
  limit: number
) => {
  return fuse.search(term.trim()).slice(0, limit);
};

const Section = styled(Grid).attrs((props: any) => ({
  backgroundColor: ['black', 'black', 'white'],
  large: true,
  display: props.open ? 'block' : 'none',
  minHeight: ['100vh', '100vh', 'auto']
}))``;

const Heading = styled(Paragraph).attrs(() => ({
  color: 'white',
  display: ['block', 'block', 'none'],
  fontSize: 24,
  mb: 20
}))``;

const InputWrap = styled(Grid).attrs(() => ({
  gridTemplateColumns: ['0 1fr auto', '0 1fr auto', 'auto 1fr auto']
}))``;

const Input = styled.input`
  ${inputReset};
  background-color: transparent;
  border-bottom: 1px ${getThemeColor('grey2')} solid;
  color: ${getThemeColor('black')};
  font-size: 16px;
  padding: 16px 26px 16px 0;
  outline: none;
  transition: border 0.3s ease-in-out;

  &:focus,
  &:focus-visible {
    border-color: ${getThemeColor('grey2')};
  }

  &::selection {
    background-color: ${getThemeColor('primary')};
    color: ${getThemeColor('white')};
  }

  ${mq.tablet(css`
    border-color: ${getThemeColor('border')};
    color: ${getThemeColor('white')};
    font-size: 14px;

    &:focus,
    &:focus-visible {
      border-color: ${getThemeColor('white')};
    }
  `)}
`;

const Header = styled(Grid).attrs(() => ({
  py: 30
}))``;

const HeaderContent = styled(Grid).attrs(() => ({
  gridTemplateColumns: [undefined, undefined, 'repeat(2, 1fr)']
}))``;

const ClearButton = styled(IconButton).attrs((props: any) => ({
  alignSelf: 'center',
  background: ['white', 'white', 'black'],
  icon: { name: 'cross', fillColor: ['black', 'black', 'white'] },
  size: 16,
  ml: -17,
  'aria-label': 'Clear search',
  title: 'Clear search',
  display: props.hidden ? 'none' : undefined
}))`
  &:not([aria-disabled='true']) {
    &:hover,
    &:active,
    &:focus {
      background-color: ${getThemeColor('primary')};

      path {
        fill: ${getThemeColor('white')};
      }
    }
  }

  svg {
    height: 6px;
    width: 6px;
  }
`;

const ResultsWrap = styled(Grid).attrs(() => ({
  gridAutoFlow: 'row',
  gridGap: 30,
  pb: [60, 60, 30]
}))``;
